import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useI18n } from "../context/I18nContext";

// ─── Zones cible ─────────────────────────────────────────────────────────────
// faceType: "full" | "trispot_recurve" | "trispot_compound"
//  - full             : blason complet 1→10, X optionnel
//  - trispot_recurve  : 6→10, 10 extérieur compté, pas de X
//  - trispot_compound : 6→10, seul le X central vaut 10 (10 ext fusionné au 9)
// Rétro-compat : booléen = withX sur blason complet ; { withX, outerTen, faceType }
export function getFaceConfig(faceType, withX = true) {
  if (faceType === "trispot_recurve") return { faceType: "trispot_recurve", withX: false, outerTen: true };
  if (faceType === "trispot_compound") return { faceType: "trispot_compound", withX: true, outerTen: false };
  return { faceType: "full", withX, outerTen: true };
}

function getTargetZones(config) {
  let withX = true, outerTen = true, faceType = "full";
  if (typeof config === "boolean") withX = config;
  else if (config && typeof config === "object") {
    withX = config.withX !== false;
    outerTen = config.outerTen !== false;
    faceType = config.faceType || "full";
  }
  const isTrispot = faceType === "trispot_recurve" || faceType === "trispot_compound";
  let zones = [
    { v: 10, isX: true,  l: "X",  c: "#FDE047", rN: 0.05 },
    { v: 10, isX: false, l: "10", c: "#FDE047", rN: 0.10 },
    { v: 9,  isX: false, l: "9",  c: "#FDE047", rN: 0.20 },
    { v: 8,  isX: false, l: "8",  c: "#EF4444", rN: 0.30 },
    { v: 7,  isX: false, l: "7",  c: "#EF4444", rN: 0.40 },
    { v: 6,  isX: false, l: "6",  c: "#60A5FA", rN: 0.50 },
    { v: 5,  isX: false, l: "5",  c: "#60A5FA", rN: 0.60 },
    { v: 4,  isX: false, l: "4",  c: "#18181b", rN: 0.70 },
    { v: 3,  isX: false, l: "3",  c: "#18181b", rN: 0.80 },
    { v: 2,  isX: false, l: "2",  c: "#d4d4d8", rN: 0.90 },
    { v: 1,  isX: false, l: "1",  c: "#d4d4d8", rN: 1.00 },
  ];
  if (!outerTen) {
    // Le 10 extérieur disparaît : sa zone fusionne avec le 9 (un seul anneau 9).
    zones = zones.filter(z => !(z.v === 10 && !z.isX));
  }
  if (isTrispot) {
    // Trispot : seulement 6→10, agrandi (l'anneau 6 remplit le blason)
    zones = zones.filter(z => z.isX || z.v >= 6);
    const maxRN = 0.50;
    zones = zones.map(z => ({ ...z, rN: z.rN / maxRN }));
  }
  zones.push({ v: 0, isX: false, l: "M", c: "#3f3f46", rN: 1.30 });
  if (!withX) {
    if (!outerTen) {
      // Ni X ni 10 extérieur : l'anneau central devient le 10.
      zones = zones.map(z => (z.isX ? { ...z, isX: false, l: "10" } : z));
    } else {
      zones = zones.filter(z => !z.isX);
    }
  }
  return zones;
}

// ─── Dessin cible (shared between modal et viewer) ────────────────────────────
function drawTargetBase(ctx, cx, cy, R, zones, scale) {
  ctx.clearRect(0, 0, cx * 2, cy * 2);
  ctx.beginPath(); ctx.arc(cx, cy, R + 2, 0, Math.PI * 2);
  ctx.fillStyle = "#111"; ctx.fill();
  for (let i = zones.length - 1; i >= 0; i--) {
    const z = zones[i];
    const r = R * z.rN * scale;
    ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = z.c; ctx.fill();
    const isBlack = z.l === "4" || z.l === "3";
    ctx.strokeStyle = isBlack ? "rgba(255,255,255,0.65)" : "rgba(0,0,0,0.22)";
    ctx.lineWidth = isBlack ? 0.8 : 0.5; ctx.stroke();
  }
  ctx.textAlign = "right"; ctx.textBaseline = "middle";
  for (const z of zones) {
    if (z.isX || z.v === 0) continue;
    const isDark = z.l === "4" || z.l === "3";
    const r = R * z.rN * scale;
    ctx.font = "bold " + (R * 0.06) + "px monospace";
    ctx.fillStyle = isDark ? "rgba(220,220,220,0.4)" : "rgba(0,0,0,0.25)";
    ctx.fillText(z.l, cx + r - 2, cy + R * 0.02);
  }
  // Libellé manqué (M) dans l'anneau extérieur
  const miss = zones.find(z => z.v === 0);
  if (miss) {
    const midR = R * (1.0 + miss.rN) / 2 * scale;
    ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.font = "bold " + (R * 0.07) + "px monospace";
    ctx.fillStyle = "rgba(255,255,255,0.5)";
    ctx.fillText("M", cx, cy - midR);
    ctx.fillText("M", cx, cy + midR);
  }
}

function drawArrowDot(ctx, ax, ay, size = 2, color = "#22d3ee") {
  ctx.beginPath(); ctx.arc(ax, ay, size, 0, Math.PI * 2);
  ctx.fillStyle = color; ctx.fill();
  ctx.lineWidth = 1.2; ctx.strokeStyle = "#000"; ctx.stroke();
}

// ─── Viewer cible (historique / session terminée) ─────────────────────────────
export function TargetViewer({ withX, config, scores, isXData, coords, onClose, dotColor = "#22d3ee", layers }) {
  const { t } = useI18n();
  const canvasRef = useRef(null);
  const zones = useMemo(() => getTargetZones(config != null ? config : withX), [config, withX]);
  const scale = 1;
  const CANVAS_R = 126;

  useEffect(() => {
    const cvs = canvasRef.current;
    if (!cvs) return;
    const ctx = cvs.getContext("2d");
    const W = cvs.width, cx = W / 2, cy = W / 2, R = CANVAS_R;
    drawTargetBase(ctx, cx, cy, R, zones, scale);
    const drawLayer = (scoresD, coordsD, color) => {
      const seriesList = Array.isArray(scoresD?.[0]?.[0]) ? scoresD : [scoresD];
      const coordsList = Array.isArray(coordsD?.[0]?.[0]) ? coordsD : [coordsD];
      seriesList.forEach((serie, si) => {
        (serie || []).forEach((end, ei) => {
          (end || []).forEach((v, ai) => {
            if (v === null || v === undefined) return;
            const c = coordsList[si]?.[ei]?.[ai];
            if (!c || (c.nx === 0.5 && c.ny === 0.5)) return; // pas de coords = placé au clavier
            const ax = cx + (c.nx - 0.5) * R * 2;
            const ay = cy + (c.ny - 0.5) * R * 2;
            drawArrowDot(ctx, ax, ay, 2, color);
          });
        });
      });
    };
    if (layers && layers.length) layers.forEach(L => drawLayer(L.scores, L.coords, L.color));
    else drawLayer(scores, coords, dotColor);
  }, [zones, scale, scores, coords, dotColor, layers]);

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 50, background: "var(--bl-overlay)", display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ width: "100%", maxWidth: "360px", background: "var(--bl-surface)", border: "1px solid var(--bl-border)", borderRadius: "16px", padding: "16px", display: "flex", flexDirection: "column", gap: "12px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ color: "#71717a", fontSize: "11px", textTransform: "uppercase", letterSpacing: ".2em" }}>{t("target.impacts_title")}</span>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#71717a", cursor: "pointer", fontSize: "22px", lineHeight: 1 }}>×</button>
        </div>
        <canvas ref={canvasRef} width={320} height={320}
          style={{ display: "block", width: "100%", aspectRatio: "1", borderRadius: "50%" }} />
        {layers && layers.length > 0 && (
          <div style={{ display: "flex", justifyContent: "center", gap: "16px", flexWrap: "wrap" }}>
            {layers.map((L, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <span style={{ width: "10px", height: "10px", borderRadius: "50%", background: L.color, border: "1px solid #000", display: "inline-block" }} />
                <span style={{ color: "#d4d4d8", fontSize: "12px" }}>{L.label}</span>
              </div>
            ))}
          </div>
        )}
        <p style={{ color: "#52525b", fontSize: "11px", textAlign: "center", textTransform: "uppercase", letterSpacing: ".1em", margin: 0 }}>
          {t("target.impacts_note")}
        </p>
      </div>
    </div>
  );
}

// ─── Modal Cible (saisie) ─────────────────────────────────────────────────────
export function TargetModal({ withX, config, maxArrows, onConfirm, onClose }) {
  const { t } = useI18n();
  const canvasRef = useRef(null);
  const zoomRef = useRef(null);
  const [arrows, setArrows] = useState([]);
  const [cursor, setCursor] = useState(null); // position du curseur en attente de validation
  const [cursorZone, setCursorZone] = useState(null);

  const zones = useMemo(() => getTargetZones(config != null ? config : withX), [config, withX]);
  const scale = 1;
  const CANVAS_R = 126;

  function hitZone(nx, ny) {
    const d = Math.sqrt((nx - 0.5) ** 2 + (ny - 0.5) ** 2) * 2;
    const dN = d / scale;
    for (const z of zones) if (dN <= z.rN) return z;
    const miss = zones.find(z => z.v === 0);
    return (dN > 1.0 && miss) ? miss : null;
  }

  const drawAll = useCallback(() => {
    const cvs = canvasRef.current;
    if (!cvs) return;
    const ctx = cvs.getContext("2d");
    const W = cvs.width, cx = W / 2, cy = W / 2, R = CANVAS_R;
    drawTargetBase(ctx, cx, cy, R, zones, scale);
    // Flèches déjà validées
    arrows.forEach(a => {
      const ax = cx + (a.nx - 0.5) * R * 2, ay = cy + (a.ny - 0.5) * R * 2;
      drawArrowDot(ctx, ax, ay, 2);
    });
    // Curseur en attente
    if (cursor && arrows.length < maxArrows) {
      const ax = cx + (cursor.nx - 0.5) * R * 2, ay = cy + (cursor.ny - 0.5) * R * 2;
      // Croix
      ctx.strokeStyle = "#22d3ee"; ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.moveTo(ax - 10, ay); ctx.lineTo(ax + 10, ay); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(ax, ay - 10); ctx.lineTo(ax, ay + 10); ctx.stroke();
      // Cercle
      ctx.beginPath(); ctx.arc(ax, ay, 7, 0, Math.PI * 2);
      ctx.strokeStyle = "#22d3ee"; ctx.lineWidth = 1.5; ctx.stroke();
    }
  }, [arrows, cursor, zones, scale, maxArrows]);

  const drawZoom = useCallback((nx, ny) => {
    const dst = zoomRef.current;
    if (!dst) return;
    const zctx = dst.getContext("2d");
    const ZW = dst.width, ZH = dst.height, Z = 3.2;
    const cx = 160, cy = 160, R = CANVAS_R;
    const px = cx + (nx - 0.5) * R * 2, py = cy + (ny - 0.5) * R * 2;
    zctx.clearRect(0, 0, ZW, ZH);
    // Reconstitution vectorielle de la zone (nette, pas un agrandissement de pixels)
    zctx.save();
    zctx.translate(ZW / 2, ZH / 2);
    zctx.scale(Z, Z);
    zctx.translate(-px, -py);
    drawTargetBase(zctx, cx, cy, R, zones, scale);
    arrows.forEach(a => {
      const ax = cx + (a.nx - 0.5) * R * 2, ay = cy + (a.ny - 0.5) * R * 2;
      drawArrowDot(zctx, ax, ay, 2);
    });
    zctx.restore();
    // Réticule
    zctx.strokeStyle = "rgba(255,255,255,0.5)"; zctx.lineWidth = 0.5;
    zctx.beginPath(); zctx.moveTo(ZW / 2, 0); zctx.lineTo(ZW / 2, ZH); zctx.stroke();
    zctx.beginPath(); zctx.moveTo(0, ZH / 2); zctx.lineTo(ZW, ZH / 2); zctx.stroke();
    zctx.beginPath(); zctx.arc(ZW / 2, ZH / 2, 9, 0, Math.PI * 2);
    zctx.strokeStyle = "#22d3ee"; zctx.lineWidth = 1.5; zctx.stroke();
  }, [zones, scale, arrows]);

  useEffect(() => {
    drawAll();
    if (cursor) drawZoom(cursor.nx, cursor.ny);
  }, [drawAll, cursor, drawZoom]);

  const dragging = useRef(false);

  function getPos(e) {
    const cvs = canvasRef.current;
    const r = cvs.getBoundingClientRect();
    const fx = (e.clientX - r.left) / r.width;
    const fy = (e.clientY - r.top) / r.height;
    // La cible est dessinée dans un rayon CANVAS_R au centre d'un canvas plus large
    // (marge autour). On dilate les coords autour du centre par (rayon canvas / rayon
    // cible) pour que l'impact tombe pile sous le doigt et que la zone détectée
    // corresponde à l'anneau réellement affiché.
    const k = (cvs.width / 2) / CANVAS_R;
    return { nx: 0.5 + (fx - 0.5) * k, ny: 0.5 + (fy - 0.5) * k };
  }

  function applyPos(e) {
    if (arrows.length >= maxArrows) return;
    const pos = getPos(e);
    setCursor(pos);
    setCursorZone(hitZone(pos.nx, pos.ny));
  }

  function onPointerDown(e) {
    e.preventDefault();
    dragging.current = true;
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
    applyPos(e);
  }
  function onPointerMove(e) {
    if (!dragging.current) return;
    e.preventDefault();
    applyPos(e);
  }
  function onPointerUp() {
    dragging.current = false;
  }

  function validateArrow() {
    if (!cursor || arrows.length >= maxArrows) return;
    const zone = hitZone(cursor.nx, cursor.ny);
    if (!zone) return;
    setArrows(prev => [...prev, { ...cursor, ...zone }]);
    setCursor(null);
    setCursorZone(null);
  }

  const total = arrows.reduce((s, a) => s + a.v, 0);
  const full = arrows.length >= maxArrows;

  function dotColor(v, isX) {
    if (isX || v >= 9) return "#FDE047";
    if (v >= 7) return "#EF4444";
    if (v >= 5) return "#60A5FA";
    return "#71717a";
  }

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 50, background: "var(--bl-overlay)", display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ width: "100%", maxWidth: "360px", background: "var(--bl-surface)", border: "1px solid var(--bl-border)", borderRadius: "16px", padding: "16px", display: "flex", flexDirection: "column", gap: "10px" }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ color: "#71717a", fontSize: "11px", textTransform: "uppercase", letterSpacing: ".2em" }}>
            {t("target.arrow")} {arrows.length + 1} / {maxArrows}
          </span>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#71717a", cursor: "pointer", fontSize: "22px", lineHeight: 1 }}>×</button>
        </div>

        {/* Zoom agrandi */}
        <div style={{ position: "relative", height: "110px", background: "#18181b", borderRadius: "8px", overflow: "hidden", border: "1px solid #27272a" }}>
          <canvas ref={zoomRef} width={328} height={110} style={{ display: "block", width: "100%", height: "110px" }} />
          {cursor && cursorZone && (
            <div style={{ position: "absolute", top: "8px", right: "10px", background: "var(--bl-chip)", borderRadius: "6px", padding: "4px 8px", display: "flex", alignItems: "center", gap: "6px" }}>
              <div style={{ width: "14px", height: "14px", borderRadius: "50%", background: dotColor(cursorZone.v, cursorZone.isX), flexShrink: 0 }} />
              <span style={{ color: "#fff", fontSize: "18px", fontWeight: 700, fontFamily: "monospace" }}>{cursorZone.l}</span>
            </div>
          )}
          {!cursor && (
            <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "#3f3f46", fontSize: "11px", textTransform: "uppercase", letterSpacing: ".15em" }}>
              {t("target.aim_hint")}
            </div>
          )}
        </div>

        {/* Cible */}
        <canvas ref={canvasRef} width={320} height={320}
          style={{ display: "block", width: "100%", aspectRatio: "1", borderRadius: "50%", touchAction: "none", cursor: "crosshair" }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        />

        {/* Bouton valider la flèche */}
        {cursor && cursorZone && !full && (
          <button onClick={validateArrow}
            style={{ width: "100%", background: dotColor(cursorZone.v, cursorZone.isX), color: cursorZone.v >= 5 ? "#000" : "#fff", border: "none", borderRadius: "8px", padding: "12px", fontSize: "14px", fontWeight: 700, cursor: "pointer", fontFamily: "monospace", letterSpacing: ".05em" }}>
            {t("target.validate")} · {cursorZone.l}
          </button>
        )}

        {/* Barre flèches placées */}
        {arrows.length > 0 && (
          <div style={{ display: "flex", gap: "5px" }}>
            {Array.from({ length: maxArrows }).map((_, i) => {
              const a = arrows[i];
              return (
                <div key={i} style={{
                  flex: 1, height: "32px", borderRadius: "6px", display: "flex", alignItems: "center", justifyContent: "center",
                  fontWeight: 700, fontSize: "12px", fontFamily: "monospace",
                  background: a ? dotColor(a.v, a.isX) : "#27272a",
                  color: a ? (a.v >= 5 ? "#000" : "#fff") : "#52525b",
                }}>
                  {a ? (a.isX ? "X" : a.v === 0 ? "M" : a.v) : "·"}
                </div>
              );
            })}
          </div>
        )}

        {/* Actions */}
        <div style={{ display: "flex", gap: "8px" }}>
          {arrows.length > 0 && (
            <button onClick={() => { setArrows(p => p.slice(0, -1)); setCursor(null); setCursorZone(null); }}
              style={{ flex: 1, background: "var(--bl-surface)", color: "var(--bl-muted)", border: "1px solid var(--bl-border)", borderRadius: "8px", padding: "10px", fontSize: "12px", cursor: "pointer" }}>
              {t("target.undo_last")}
            </button>
          )}
          {full && (
            <button onClick={() => { onConfirm(arrows); onClose(); }}
              style={{ flex: 2, background: "#22d3ee", color: "#000", border: "none", borderRadius: "8px", padding: "10px", fontSize: "13px", fontWeight: 700, cursor: "pointer" }}>
              {t("target.confirm_end")} · {total} {t("target.pts")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
